// ScanWizardPage — /liff/officer/scan/wizard?submissions=<sid1,sid2,...>
//
// Spec: docs/specs/location-tasks.md §8.3.
//
// Thin LIFF chrome around <WizardRunner>. Receives one or more
// submission IDs from the public /scan SPA (which produced them via
// /v1/patrols/scan/finalize), constructs a LIFF wizard ctx (JWT auth
// + /v1/patrols/submissions/* paths), and lets the shared runner do
// the rest.
//
// The public scan SPA also uses WizardRunner — see
// ScanWizardPublicPage's finalized state — but with a scan_session
// ctx instead.

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import liff from "@line/liff";
import { useLiff } from "../../../liff/index.js";
import { MobileShell, Card, EmptyMobile, PrimaryButton, SecondaryButton } from "../../../liff/components/Mobile.js";
import WizardRunner from "../wizard/WizardRunner.js";
import StepDeviceVerify from "../wizard/StepDeviceVerify.js";
import StepFaceVerify from "../wizard/StepFaceVerify.js";
import StepGeoVerify from "../wizard/StepGeoVerify.js";
import StepTextResponse from "../wizard/StepTextResponse.js";
import StepCameraEvidence from "../wizard/StepCameraEvidence.js";
import StepFileEvidence from "../wizard/StepFileEvidence.js";
import StepCanvasSignature from "../wizard/StepCanvasSignature.js";
import { liffWizardCtx } from "../wizard/types.js";

export function ScanWizardPage(): React.ReactElement {
  const { session } = useLiff();
  const [params] = useSearchParams();
  const submissionIds = useMemo(() => parseIds(params.get("submissions")), [params]);
  const [done, setDone] = useState(false);

  if (!submissionIds.length) {
    return (
      <MobileShell title="任務精靈">
        <EmptyMobile title="缺少任務提交參數" body="此頁需從加密巡邏標籤感應成功後跳轉而來。" />
      </MobileShell>
    );
  }

  if (!session?.session_token) {
    return (
      <MobileShell title="任務精靈">
        <EmptyMobile title="未登入" body="請先回 LINE Bot 點「我的綁定」完成登入。" />
      </MobileShell>
    );
  }

  const ctx = liffWizardCtx(session.session_token);

  const doneScreen = (
    <MobileShell title="任務完成">
      <Card>
        <div className="flex flex-col items-center text-center py-4">
          <CheckCircle2 className="h-12 w-12 text-success mb-3" />
          <p className="text-base font-medium mb-1">本次掃描的 {submissionIds.length} 個任務全部處理完成</p>
          <p className="text-xs text-text-muted">您可以關閉此頁，或回到首頁查看歷程紀錄。</p>
        </div>
      </Card>
      <PrimaryButton onClick={() => closeOrGo("/liff/officer/history")}>查看歷程</PrimaryButton>
      <SecondaryButton onClick={() => closeOrGo("/liff/me")}>回首頁</SecondaryButton>
    </MobileShell>
  );

  if (done) return doneScreen;

  return (
    <WizardRunner
      ctx={ctx}
      submissionIds={submissionIds}
      onAllDone={() => setDone(true)}
      doneScreen={doneScreen}
      renderDevice={({ data: d, onComplete }) => <StepDeviceVerify data={d} onComplete={onComplete} />}
      renderFace={({ data: d, onComplete }) => <StepFaceVerify data={d} onComplete={onComplete} />}
      renderGeo={({ data: d, onComplete }) => <StepGeoVerify data={d} onComplete={onComplete} />}
      renderText={({ text, onTextChange }) => <StepTextResponse text={text} onTextChange={onTextChange} />}
      renderCamera={({ data: d, onComplete }) => <StepCameraEvidence ctx={ctx} data={d} onComplete={onComplete} />}
      renderFile={({ data: d, onComplete }) => <StepFileEvidence ctx={ctx} data={d} onComplete={onComplete} />}
      renderCanvas={({ data: d, onComplete }) => <StepCanvasSignature ctx={ctx} data={d} onComplete={onComplete} />}
      chrome={({ title, subtitle, body, footer }) => (
        <MobileShell title={title} subtitle={subtitle} footer={footer}>
          {body}
        </MobileShell>
      )}
    />
  );
}

function parseIds(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function closeOrGo(fallback: string): void {
  try {
    if (typeof liff !== "undefined" && liff.isInClient && liff.isInClient()) {
      liff.closeWindow();
      return;
    }
  } catch { /* not in LIFF */ }
  window.location.href = fallback;
}
